import { supabase } from '@/lib/supabase';
import type { Assessment, AssessmentResult, Question } from '@/types/database';

// Service for Skills Assessment database operations
export class SkillsAssessmentService {
  // ===== ASSESSMENTS =====
  
  static async getAssessments(): Promise<Assessment[]> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const organizationId = await this.getUserOrganizationId();
      if (!organizationId) {
        // Return empty array if user has no organization yet
        return [];
      }

      const { data, error } = await supabase
        .from('skills_assessments')
        .select(`
          *,
          assessment_questions (*)
        `)
        .eq('organization_id', organizationId)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      
      // Transform database format to component format
      return (data || []).map(assessment => ({
        id: assessment.id,
        name: assessment.name,
        type: assessment.assessment_type,
        difficulty: assessment.difficulty_level,
        duration: assessment.duration_minutes,
        skills: assessment.skills_assessed || [],
        questions: assessment.assessment_questions?.map((q: any) => ({
          id: q.id,
          type: q.question_type,
          question: q.question_text,
          options: q.question_options || [],
          correct: q.correct_answer,
          points: q.points || 10,
          explanation: q.explanation,
          timeLimit: q.time_limit_minutes,
          template: q.code_template,
          expectedOutput: q.expected_output
        })) || [],
        createdAt: assessment.created_at,
        lastUsed: assessment.last_used_at,
        timesUsed: assessment.times_used || 0
      }));
    } catch (error) {
      console.error('Error fetching assessments:', error);
      return [];
    }
  }

  static async saveAssessment(assessmentData: {
    name: string;
    type: string;
    difficulty: string;
    duration: number;
    skills: string[];
    questions: Question[];
  }): Promise<Assessment | null> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const organizationId = await this.getUserOrganizationId();
      if (!organizationId) {
        throw new Error('User must be part of an organization to create assessments');
      }

      // Insert assessment
      const { data: assessment, error: assessmentError } = await supabase
        .from('skills_assessments')
        .insert({
          user_id: user.id,
          organization_id: organizationId,
          name: assessmentData.name,
          assessment_type: assessmentData.type,
          difficulty_level: assessmentData.difficulty,
          duration_minutes: assessmentData.duration,
          skills_assessed: assessmentData.skills,
          is_active: true,
          is_public: false
        })
        .select()
        .maybeSingle();

      if (assessmentError) throw assessmentError;

      // Insert questions
      if (assessmentData.questions && assessmentData.questions.length > 0) {
        const questionsToInsert = assessmentData.questions.map((question, index) => ({
          assessment_id: assessment.id,
          question_order: index + 1,
          question_type: question.type,
          question_text: question.question,
          question_options: question.options || [],
          correct_answer: question.correct,
          points: question.points || 10,
          explanation: question.explanation,
          time_limit_minutes: question.timeLimit,
          code_template: question.template,
          expected_output: question.expectedOutput
        }));

        const { error: questionsError } = await supabase
          .from('assessment_questions')
          .insert(questionsToInsert);

        if (questionsError) throw questionsError;
      }

      // Return in component format
      return {
        id: assessment.id,
        name: assessment.name,
        type: assessment.assessment_type,
        difficulty: assessment.difficulty_level,
        duration: assessment.duration_minutes,
        skills: assessment.skills_assessed || [],
        questions: assessmentData.questions,
        createdAt: assessment.created_at,
        timesUsed: 0
      };
    } catch (error) {
      console.error('Error saving assessment:', error);
      return null;
    }
  }

  static async deleteAssessment(assessmentId: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('skills_assessments')
        .delete()
        .eq('id', assessmentId);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error deleting assessment:', error);
      return false;
    }
  }

  // ===== ASSESSMENT RESULTS =====
  
  static async getAssessmentResults(): Promise<AssessmentResult[]> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const organizationId = await this.getUserOrganizationId();
      if (!organizationId) {
        // Return empty array if user has no organization yet
        return [];
      }

      const { data, error } = await supabase
        .from('assessment_results')
        .select(`
          *,
          skills_assessments (
            name,
            skills_assessed
          )
        `)
        .eq('organization_id', organizationId)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      
      // Transform database format to component format
      return (data || []).map(result => ({
        id: result.id,
        assessmentId: result.assessment_id,
        candidateName: result.candidate_name,
        candidateEmail: result.candidate_email || '',
        score: result.earned_points,
        maxScore: result.total_points,
        percentage: result.percentage_score,
        completedAt: result.completed_at || result.created_at,
        timeSpent: result.time_spent_minutes || 0,
        answers: result.answers || [],
        skillsBreakdown: this.generateSkillsBreakdown(
          result.skills_assessments?.skills_assessed || [],
          result.percentage_score
        ),
        status: result.status as any,
        notes: result.feedback
      }));
    } catch (error) {
      console.error('Error fetching assessment results:', error);
      return [];
    }
  }

  static async saveAssessmentResult(resultData: {
    assessmentId: string;
    candidateName: string;
    candidateEmail: string;
    score: number;
    maxScore: number;
    percentage: number;
    timeSpent: number;
    notes?: string;
  }): Promise<AssessmentResult | null> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const organizationId = await this.getUserOrganizationId();

      const { data, error } = await supabase
        .from('assessment_results')
        .insert({
          assessment_id: resultData.assessmentId,
          candidate_name: resultData.candidateName,
          candidate_email: resultData.candidateEmail,
          organization_id: organizationId,
          total_points: resultData.maxScore,
          earned_points: resultData.score,
          time_spent_minutes: resultData.timeSpent,
          status: 'completed',
          feedback: resultData.notes,
          completed_at: new Date().toISOString(),
          submitted_at: new Date().toISOString()
        })
        .select()
        .maybeSingle();

      if (error) throw error;

      // Update assessment usage stats using RPC function
      await supabase
        .rpc('increment_assessment_usage', {
          assessment_id: resultData.assessmentId
        });

      // Return in component format
      return {
        id: data.id,
        assessmentId: data.assessment_id,
        candidateName: data.candidate_name,
        candidateEmail: data.candidate_email || '',
        score: data.earned_points,
        maxScore: data.total_points,
        percentage: data.percentage_score,
        completedAt: data.completed_at || data.created_at,
        timeSpent: data.time_spent_minutes || 0,
        answers: [],
        skillsBreakdown: [],
        status: 'completed',
        notes: data.feedback
      };
    } catch (error) {
      console.error('Error saving assessment result:', error);
      return null;
    }
  }

  static async updateAssessmentResult(
    resultId: string,
    updates: Partial<{
      candidateName: string;
      candidateEmail: string;
      score: number;
      maxScore: number;
      percentage: number;
      timeSpent: number;
      notes?: string;
    }>
  ): Promise<boolean> {
    try {
      const updateData: any = {};
      
      if (updates.candidateName) updateData.candidate_name = updates.candidateName;
      if (updates.candidateEmail) updateData.candidate_email = updates.candidateEmail;
      if (updates.score !== undefined) updateData.earned_points = updates.score;
      if (updates.maxScore !== undefined) updateData.total_points = updates.maxScore;
      if (updates.timeSpent !== undefined) updateData.time_spent_minutes = updates.timeSpent;
      if (updates.notes !== undefined) updateData.feedback = updates.notes;

      const { error } = await supabase
        .from('assessment_results')
        .update(updateData)
        .eq('id', resultId);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error updating assessment result:', error);
      return false;
    }
  }

  static async deleteAssessmentResult(resultId: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('assessment_results')
        .delete()
        .eq('id', resultId);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error deleting assessment result:', error);
      return false;
    }
  }

  // ===== HELPER METHODS =====
  
  private static async getUserOrganizationId(): Promise<string | null> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;

      const { data: member } = await supabase
        .from('organization_members')
        .select('organization_id')
        .eq('user_id', user.id)
        .maybeSingle();

      return member?.organization_id || null;
    } catch (error) {
      console.error('Error getting user organization:', error);
      return null;
    }
  }

  private static generateSkillsBreakdown(skills: string[], percentage: number): Array<{
    skill: string;
    score: number;
    maxScore: number;
    percentage: number;
  }> {
    return skills.map(skill => ({
      skill,
      score: Math.round((percentage / 100) * 10),
      maxScore: 10,
      percentage
    }));
  }
} 